import { HttpError } from '@/lib/httpError'
import { toApiError } from '@/lib/apiError'

interface User {
  id: string
  account: string
  name: string
  platform_role: 'owner' | 'admin' | 'auditor' | 'security' | 'user'
  type: 'local' | 'ldap' | 'oidc'
  created_at: string
  updated_at: string
  last_login_at?: string
  external_id?: string
  /** True while the account is still on a password somebody else set. */
  must_change_password?: boolean
  /** True while an administrative account has no second factor registered. */
  must_enrol_totp?: boolean
  totp_enabled?: boolean
  contract_agreement?: boolean
}

interface ListUsersResponse {
  users: User[]
  total: number
  limit: number
  offset: number
}

interface ListUsersParams {
  limit?: number
  offset?: number
  q?: string
}

interface CreateUserRequest {
  account: string
  name: string
  role?: 'owner' | 'admin' | 'auditor' | 'security' | 'user'
}

/**
 * What creating a user returns.
 *
 * The account arrives with no password and cannot be signed into. `code` is the
 * one-time credential the owner hands over so the holder can choose their own,
 * and it is shown exactly once. It is absent only when issuing it failed, in
 * which case the account exists and a code can be issued from the user list.
 */
interface CreatedUser {
  user: User
  /** Absent only if generating it failed; the owner can then reset instead. */
  temporary_password?: string
}

/**
 * A temporary password an administrator hands over.
 *
 * Shown exactly once, stored nowhere. The account it belongs to is flagged, so
 * its holder must replace it at first sign-in before the session can reach
 * anything else.
 */
interface TemporaryPassword {
  temporary_password: string
  account?: string
  /** RFC 3339; the temporary password signs in once, before this. */
  expires_at?: string
  /** Whether the account's own password was revoked along with the reset. */
  revoked_current?: boolean
}

class UserService {
  async getUsers(params?: ListUsersParams): Promise<ListUsersResponse> {
    const queryParams = new URLSearchParams()

    if (params?.limit !== undefined) {
      queryParams.append('limit', params.limit.toString())
    }
    if (params?.offset !== undefined) {
      queryParams.append('offset', params.offset.toString())
    }
    if (params?.q) {
      queryParams.append('q', params.q)
    }

    const endpoint = `/api/v2/users${queryParams.toString() ? `?${queryParams}` : ''}`
    const response = await fetch(endpoint, {
      method: 'GET',
      credentials: 'include',
    })

    if (!response.ok) {
      switch (response.status) {
        case 401:
          throw new HttpError('errors.unauthorized', 401)
        case 403:
          throw new HttpError('errors.forbidden', 403)
        case 500:
          throw new HttpError('errors.serverError', 500)
        default:
          throw new HttpError('errors.getUsersFailed', response.status)
      }
    }

    const data: ListUsersResponse = await response.json()
    return data
  }

  async getUserById(id: string): Promise<User> {
    const response = await fetch(`/api/v2/users/${id}`, {
      method: 'GET',
      credentials: 'include',
    })

    if (!response.ok) {
      switch (response.status) {
        case 401:
          throw new Error('errors.unauthorized')
        case 403:
          throw new Error('errors.forbidden')
        case 404:
          throw new Error('errors.userNotFound')
        case 500:
          throw new Error('errors.serverError')
        default:
          throw new Error('errors.getUserFailed')
      }
    }

    return response.json()
  }

  async createUser(userData: CreateUserRequest): Promise<CreatedUser> {
    const response = await fetch('/api/v2/users', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(userData),
    })

    if (!response.ok) {
      throw await toApiError(response, 'errors.createUserFailed')
    }

    return (await response.json()) as CreatedUser
  }

  // Admin or owner files a user-creation request
  async requestUserCreate(userData: CreateUserRequest): Promise<void> {
    const response = await fetch('/api/v2/users/request', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(userData),
    })

    if (!response.ok) {
      throw await toApiError(response, 'errors.requestCreateUserFailed')
    }
  }

  async deleteUser(id: string): Promise<void> {
    const response = await fetch(`/api/v2/users/${id}`, {
      method: 'DELETE',
      credentials: 'include',
    })

    if (!response.ok) {
      switch (response.status) {
        case 400:
          throw new Error('errors.badRequest')
        case 401:
          throw new Error('errors.unauthorized')
        case 403:
          throw new Error('errors.forbidden')
        case 404:
          throw new Error('errors.userNotFound')
        case 500:
          throw new Error('errors.serverError')
        default:
          throw new Error('errors.deleteUserFailed')
      }
    }
  }

  // Owner changes the role outright
  async updateUserRole(id: string, role: 'owner' | 'admin' | 'auditor' | 'security' | 'user'): Promise<void> {
    const response = await fetch(`/api/v2/users/${id}/role`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ role }),
    })

    if (!response.ok) {
      switch (response.status) {
        case 400:
          throw new Error('errors.badRequest')
        case 401:
          throw new Error('errors.unauthorized')
        case 403:
          throw new Error('errors.forbidden')
        case 404:
          throw new Error('errors.userNotFound')
        case 500:
          throw new Error('errors.serverError')
        default:
          throw new Error('errors.updateUserFailed')
      }
    }
  }

  // Admin files a role-change request (an owner may use this too)
  async requestUserRoleChange(id: string, role: 'owner' | 'admin' | 'auditor' | 'security' | 'user'): Promise<void> {
    const response = await fetch(`/api/v2/users/${id}/role/request`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ role }),
    })

    if (!response.ok) {
      switch (response.status) {
        case 400:
          throw new Error('errors.badRequest')
        case 401:
          throw new Error('errors.unauthorized')
        case 403:
          throw new Error('errors.forbidden')
        case 404:
          throw new Error('errors.userNotFound')
        case 500:
          throw new Error('errors.serverError')
        default:
          throw new Error('errors.requestRoleChangeFailed')
      }
    }
  }

  /**
   * Reset a user's password.
   *
   * The platform generates the replacement; the owner relays it and never
   * chooses it, because a password an owner picks is one they may reuse across
   * every account they reset. The person's own password keeps working unless
   * revokeCurrent is set, which also ends every session it opened.
   *
   * The value comes back exactly once and is stored nowhere, so a copy lost
   * before handover means resetting again.
   */
  async resetPassword(id: string, revokeCurrent = false): Promise<TemporaryPassword> {
    const response = await fetch(`/api/v2/users/${id}/reset-password`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ revoke_current: revokeCurrent }),
    })

    if (!response.ok) {
      throw await toApiError(response, 'passwordReset.owner.failed')
    }

    return (await response.json()) as TemporaryPassword
  }

  /**
   * Clear a user's second factor.
   *
   * The way back for somebody who lost the device and the recovery codes with
   * it. Without it, requiring the factor turns every lost phone into a
   * permanently unreachable account.
   *
   * The account drops to a password alone, which is the state the factor
   * existed to prevent, so its sessions end and it meets the enrolment gate
   * again at the next sign-in.
   */
  async clearTOTP(id: string): Promise<void> {
    const response = await fetch(`/api/v2/users/${id}/totp`, {
      method: 'DELETE',
      credentials: 'include',
    })

    if (!response.ok) {
      throw await toApiError(response, 'errors.clearTOTPFailed')
    }
  }
}

export const userService = new UserService()
export type { User, CreateUserRequest, CreatedUser, ListUsersResponse, ListUsersParams, TemporaryPassword }
